import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Pencil, Phone, ShieldCheck, UserPlus, Users } from "lucide-react";
import { api } from "../../lib/api";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { apiErrorMessage } from "../../components/admin/PushScheduling";

// Keep dialogs inside small screens and the phone's safe areas.
const DIALOG_FIT =
  "w-[calc(100%-2rem)] max-h-[calc(100dvh-2rem-env(safe-area-inset-top)-env(safe-area-inset-bottom))] overflow-y-auto";

const FILTERS = [
  { key: "all", label: "All", count: (u) => u.total },
  { key: "active", label: "Active", count: (u) => u.active },
  { key: "inactive", label: "Inactive", count: (u) => u.inactive },
];

const fmtDate = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime())
    ? d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })
    : "";
};

/** Strip formatting so "+91 98765 43210" validates like the server does. */
const normalisePhone = (raw) => {
  let d = (raw || "").replace(/[\s\-()+]/g, "");
  if (d.length === 12 && d.startsWith("91")) d = d.slice(2);
  else if (d.length === 11 && d.startsWith("0")) d = d.slice(1);
  return d;
};

function StatusBadge({ member }) {
  const active = member.status === "active";
  return (
    <span
      className={`inline-flex items-center border px-2 py-0.5 text-[11px] font-medium shrink-0 ${active ? "bg-green-50 text-green-800 border-green-200" : "bg-neutral-100 text-neutral-600 border-neutral-300"}`}
      data-testid={`staff-status-${member.id}`}
      data-status={member.status}
    >
      {active ? "Active" : "Inactive"}
    </span>
  );
}

function AddStaffDialog({ open, onOpenChange, onAdded }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) { setName(""); setPhone(""); setError(null); }
  }, [open]);

  const cleanPhone = normalisePhone(phone);
  const phoneOk = /^\d{10}$/.test(cleanPhone);
  const canSave = name.trim() && phoneOk && !saving;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const { data } = await api.post("/admin/staff", { name: name.trim(), phone: cleanPhone });
      toast.success(data.promoted_customer
        ? `${data.staff.name || "This customer"} is now an admin of your store`
        : `${data.staff.name} added as an admin`);
      onAdded(data);
      onOpenChange(false);
    } catch (err) {
      setError(apiErrorMessage(err, "Could not add this admin"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !saving && onOpenChange(v)}>
      <DialogContent className={`max-w-md ${DIALOG_FIT}`} data-testid="staff-add-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Add an admin</DialogTitle>
          <DialogDescription>
            They sign in on your store with this mobile number (OTP) and get the same access you have.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <div>
            <label htmlFor="staff-name" className="text-sm font-medium">Name <span className="text-red-500">*</span></label>
            <input id="staff-name" className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1 min-h-[44px]"
              value={name} onChange={(e) => setName(e.target.value)} maxLength={80}
              placeholder="Full name" autoComplete="name" data-testid="staff-name-input" />
          </div>
          <div>
            <label htmlFor="staff-phone" className="text-sm font-medium">Mobile number <span className="text-red-500">*</span></label>
            <input id="staff-phone" type="tel" inputMode="numeric"
              className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1 min-h-[44px]"
              value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={20}
              placeholder="10-digit mobile number" autoComplete="tel"
              aria-invalid={!!phone && !phoneOk} data-testid="staff-phone-input" />
            {phone && !phoneOk && (
              <div className="text-xs text-red-600 mt-1" role="alert" data-testid="staff-phone-error">
                Enter a valid 10-digit mobile number.
              </div>
            )}
          </div>
          {error && (
            <div className="text-sm border border-red-200 bg-red-50 text-red-700 p-2 flex gap-2" role="alert" data-testid="staff-add-error">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" /> <span>{error}</span>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <button type="button" className="kb-btn-outline w-full sm:w-auto" disabled={saving}
              onClick={() => onOpenChange(false)} data-testid="staff-add-cancel">Cancel</button>
            <button type="submit" disabled={!canSave}
              className="kb-btn-primary inline-flex items-center justify-center gap-2 w-full sm:w-auto"
              data-testid="staff-add-submit">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
              {saving ? "Adding…" : "Add admin"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditStaffDialog({ member, onOpenChange, onSaved }) {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (member) { setName(member.name || ""); setError(null); }
  }, [member]);

  const clean = name.trim().replace(/\s+/g, " ");
  const canSave = !!clean && clean !== (member?.name || "") && !saving;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const { data } = await api.put(`/admin/staff/${member.id}`, { name: clean });
      toast.success(`${data.staff.name}'s details updated`);
      onSaved(data);
      onOpenChange(false);
    } catch (err) {
      setError(apiErrorMessage(err, "Could not update this admin"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!member} onOpenChange={(v) => !saving && onOpenChange(v)}>
      <DialogContent className={`max-w-md ${DIALOG_FIT}`} data-testid="staff-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Edit admin</DialogTitle>
          <DialogDescription>Update this admin's details. Their access is not affected.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <div>
            <label htmlFor="staff-edit-name" className="text-sm font-medium">Name <span className="text-red-500">*</span></label>
            <input id="staff-edit-name" className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1 min-h-[44px]"
              value={name} onChange={(e) => setName(e.target.value)} maxLength={80}
              autoComplete="name" data-testid="staff-edit-name-input" />
          </div>
          <div>
            <label htmlFor="staff-edit-phone" className="text-sm font-medium">Mobile number</label>
            <input id="staff-edit-phone" readOnly disabled value={member?.phone || ""}
              className="w-full border border-[hsl(var(--kb-border))] bg-[hsl(var(--kb-surface-alt))] text-[hsl(var(--kb-muted))] p-2 text-sm mt-1 min-h-[44px] font-mono"
              data-testid="staff-edit-phone-input" />
            <div className="text-xs text-[hsl(var(--kb-muted))] mt-1" data-testid="staff-edit-phone-hint">
              The mobile number is this admin's login and can't be changed. To use a new number,
              deactivate this admin and add the new number.
            </div>
          </div>
          {error && (
            <div className="text-sm border border-red-200 bg-red-50 text-red-700 p-2 flex gap-2" role="alert" data-testid="staff-edit-error">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" /> <span>{error}</span>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <button type="button" className="kb-btn-outline w-full sm:w-auto" disabled={saving}
              onClick={() => onOpenChange(false)} data-testid="staff-edit-cancel">Cancel</button>
            <button type="submit" disabled={!canSave}
              className="kb-btn-primary inline-flex items-center justify-center gap-2 w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="staff-edit-submit">
              {saving && <Loader2 size={14} className="animate-spin" />}
              {saving ? "Saving…" : "Save changes"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function StaffRow({ member, busy, canActivate, onEdit, onDeactivate, onActivate }) {
  return (
    <li className="border border-[hsl(var(--kb-border))] bg-white p-4" data-testid={`staff-row-${member.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-medium break-words [overflow-wrap:anywhere]" data-testid={`staff-name-${member.id}`}>
              {member.name || "Unnamed"}
            </span>
            {member.is_primary && (
              <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 bg-[hsl(var(--kb-surface-alt))]" data-testid={`staff-primary-${member.id}`}>
                <ShieldCheck size={11} /> Primary admin
              </span>
            )}
            {member.is_you && <span className="text-[11px] px-2 py-0.5 bg-blue-50 text-blue-800">You</span>}
          </div>
          <div className="text-xs text-[hsl(var(--kb-muted))] mt-1 flex items-center gap-1.5">
            <Phone size={11} className="shrink-0" /> <span className="font-mono">{member.phone}</span>
          </div>
          <div className="text-[11px] text-[hsl(var(--kb-muted))] mt-1">
            Added {fmtDate(member.added_at)}{member.added_by ? ` by ${member.added_by}` : ""}
            {member.status === "inactive" && member.status_changed_at ? ` · deactivated ${fmtDate(member.status_changed_at)}` : ""}
          </div>
        </div>
        <StatusBadge member={member} />
      </div>
      {(member.can_edit || member.can_deactivate || member.can_activate) && (
        <div className="mt-3 flex flex-col sm:flex-row gap-2">
          {member.can_edit && (
            <button type="button" disabled={busy} onClick={() => onEdit(member)}
              className="kb-btn-outline text-xs !py-2 min-h-[40px] w-full sm:w-auto inline-flex items-center justify-center gap-1.5"
              data-testid={`staff-edit-${member.id}`}>
              <Pencil size={12} /> Edit
            </button>
          )}
          {member.can_deactivate ? (
            <button type="button" disabled={busy} onClick={() => onDeactivate(member)}
              className="kb-btn-outline text-xs !py-2 min-h-[40px] w-full sm:w-auto inline-flex items-center justify-center gap-1.5"
              data-testid={`staff-deactivate-${member.id}`}>
              {busy && <Loader2 size={12} className="animate-spin" />} Deactivate
            </button>
          ) : member.can_activate && (
            <button type="button" disabled={busy || !canActivate} onClick={() => onActivate(member)}
              title={canActivate ? "" : "Admin limit reached"}
              className="kb-btn-primary text-xs !py-2 min-h-[40px] w-full sm:w-auto inline-flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid={`staff-activate-${member.id}`}>
              {busy && <Loader2 size={12} className="animate-spin" />} Activate
            </button>
          )}
        </div>
      )}
    </li>
  );
}

export default function AdminStaff() {
  const [filter, setFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [confirming, setConfirming] = useState(null);
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async (which) => {
    try {
      const { data } = await api.get("/admin/staff", { params: { status: which } });
      setItems(data.items || []);
      setUsage(data.usage);
      setError(null);
    } catch (e) {
      setError(apiErrorMessage(e, "Could not load your team"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(filter); }, [filter, load]);

  const change = async (member, action) => {
    setBusyId(member.id);
    try {
      await api.post(`/admin/staff/${member.id}/${action}`);
      toast.success(action === "deactivate"
        ? `${member.name || "Admin"} deactivated — they can no longer sign in`
        : `${member.name || "Admin"} reactivated`);
    } catch (e) {
      toast.error(apiErrorMessage(e, action === "deactivate" ? "Could not deactivate" : "Could not activate"));
    } finally {
      setBusyId(null);
      load(filter);
    }
  };

  const atLimit = usage && !usage.can_add;

  return (
    <div className="space-y-6" data-testid="admin-staff-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div className="min-w-0">
          <h1 className="font-display text-3xl">Staff &amp; Admins</h1>
          <p className="text-sm text-[hsl(var(--kb-muted))] mt-1 max-w-xl">
            Everyone here has full admin access to your store. Deactivating someone blocks their
            sign-in immediately and keeps their account and history.
          </p>
        </div>
        <div className="flex flex-col sm:items-end gap-2 shrink-0">
          {usage && (
            <div className={`inline-flex items-center gap-2 border px-3 py-1.5 text-sm self-start sm:self-auto ${usage.over_limit ? "border-red-300 bg-red-50 text-red-800" : atLimit ? "border-amber-300 bg-amber-50 text-amber-900" : "border-[hsl(var(--kb-border))] bg-white"}`}
              data-testid="staff-usage">
              <Users size={14} /> <b>{usage.active} / {usage.limit}</b> Admins
            </div>
          )}
          <button type="button" onClick={() => setAddOpen(true)} disabled={!usage || atLimit}
            className="kb-btn-primary inline-flex items-center justify-center gap-2 min-h-[44px] w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="staff-add-btn">
            <UserPlus size={14} /> Add Staff / Admin
          </button>
        </div>
      </div>

      {atLimit && (
        <div className="border border-amber-300 bg-amber-50 text-amber-900 px-4 py-3 text-sm flex items-start gap-2" data-testid="staff-limit-notice">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>
            {usage.over_limit
              ? `Your store has ${usage.active} active admins but its limit is ${usage.limit}. Nobody was removed, but you can't add or reactivate admins until you are within the limit.`
              : `You've reached your limit of ${usage.limit} admin${usage.limit === 1 ? "" : "s"}. Deactivate an admin to free a seat, or contact the platform team to raise your limit.`}
          </span>
        </div>
      )}

      {error && (
        <div className="border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm" data-testid="staff-load-error">{error}</div>
      )}

      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filter admins" data-testid="staff-filters">
        {FILTERS.map((f) => (
          <button key={f.key} type="button" role="tab" aria-selected={filter === f.key}
            onClick={() => setFilter(f.key)}
            className={`border px-3 py-2 text-sm min-h-[40px] ${filter === f.key ? "border-[hsl(var(--kb-primary))] bg-[hsl(var(--kb-primary))]/5 font-medium" : "border-[hsl(var(--kb-border))] bg-white"}`}
            data-testid={`staff-filter-${f.key}`}>
            {f.label}{usage ? ` (${f.count(usage)})` : ""}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3" data-testid="staff-loading">
          {[0, 1].map((i) => <div key={i} className="h-24 bg-[hsl(var(--kb-surface-alt))] animate-pulse" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-[hsl(var(--kb-border))] p-8 text-center text-sm text-[hsl(var(--kb-muted))]" data-testid="staff-empty">
          {filter === "inactive" ? "No inactive admins." : filter === "active" ? "No active admins." : "No admins yet."}
        </div>
      ) : (
        <ul className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="staff-list">
          {items.map((m) => (
            <StaffRow key={m.id} member={m} busy={busyId === m.id} canActivate={!!usage?.can_add}
              onEdit={setEditing} onDeactivate={setConfirming} onActivate={(x) => change(x, "activate")} />
          ))}
        </ul>
      )}

      <AddStaffDialog open={addOpen} onOpenChange={setAddOpen} onAdded={() => load(filter)} />
      <EditStaffDialog member={editing} onOpenChange={(v) => !v && setEditing(null)} onSaved={() => load(filter)} />

      <AlertDialog open={!!confirming} onOpenChange={(v) => !v && setConfirming(null)}>
        <AlertDialogContent className={`max-w-md ${DIALOG_FIT}`} data-testid="staff-deactivate-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate {confirming?.name || "this admin"}?</AlertDialogTitle>
            <AlertDialogDescription>
              They will be signed out and won't be able to log in to your store until you
              reactivate them. Their account and history are kept.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-2">
            <AlertDialogCancel className="w-full sm:w-auto mt-0" data-testid="staff-deactivate-keep">Keep active</AlertDialogCancel>
            <AlertDialogAction className="w-full sm:w-auto" data-testid="staff-deactivate-confirm"
              onClick={() => { const m = confirming; setConfirming(null); change(m, "deactivate"); }}>
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
